import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import authService from '../services/auth.service';
import { toast } from 'react-toastify';
import { getAndClearRedirectPath } from '../utils/redirect';

const Login: React.FC = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberPassword: false,
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await authService.login({
        email: formData.email,
        password: formData.password,
      });

      // Check if there's a saved redirect path
      const redirectPath = getAndClearRedirectPath();
      console.log('Login successful, redirect path:', redirectPath);

      // Use window.location.href to ensure proper redirect
      const finalRedirect = redirectPath || '/dashboard';
      console.log('Final redirect to:', finalRedirect);
      window.location.href = finalRedirect;
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to login');
    } finally {
      setLoading(false);
    }
  };

  const handleSocialLogin = async (provider: string) => {
    try {
      const redirectUrl = await authService.socialLogin(provider);
      window.location.href = redirectUrl;
    } catch (error: any) {
      toast.error(`Failed to initialize ${provider} login`);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  return (
    <>
      <h4 className='fw-semibold mb-2'>Sign In</h4>
      <p className='mb-4 text-muted op-7 fw-normal'>Welcome back!</p>

      <div className='btn-list'>
        <button
          className='btn btn-light'
          onClick={() => handleSocialLogin('google')}
          type='button'
        >
          <svg
            className='google-svg'
            xmlns='http://www.w3.org/2000/svg'
            width='2443'
            height='2500'
            preserveAspectRatio='xMidYMid'
            viewBox='0 0 256 262'
          >
            <path
              fill='#4285F4'
              d='M255.878 133.451c0-10.734-.871-18.567-2.756-26.69H130.55v48.448h71.947c-1.45 12.04-9.283 30.172-26.69 42.356l-.244 1.622 38.755 30.023 2.685.268c24.659-22.774 38.875-56.282 38.875-96.027'
            />
            <path
              fill='#34A853'
              d='M130.55 261.1c35.248 0 64.839-11.605 86.453-31.622l-41.196-31.913c-11.024 7.688-25.82 13.055-45.257 13.055-34.523 0-63.824-22.773-74.269-54.25l-1.531.13-40.298 31.187-.527 1.465C35.393 231.798 79.49 261.1 130.55 261.1'
            />
            <path
              fill='#FBBC05'
              d='M56.281 156.37c-2.756-8.123-4.351-16.827-4.351-25.82 0-8.994 1.595-17.697 4.206-25.82l-.073-1.73L15.26 71.312l-1.335.635C5.077 89.644 0 109.517 0 130.55s5.077 40.905 13.925 58.602l42.356-32.782'
            />
            <path
              fill='#EB4335'
              d='M130.55 50.479c24.514 0 41.05 10.589 50.479 19.438l36.844-35.974C195.245 12.91 165.798 0 130.55 0 79.49 0 35.393 29.301 13.925 71.947l42.211 32.783c10.59-31.477 39.891-54.251 74.414-54.251'
            />
          </svg>
          Sign in with Google
        </button>
        <button
          className='btn btn-icon btn-light'
          onClick={() => handleSocialLogin('microsoft')}
          type='button'
        >
          <i className='ri-microsoft-fill'></i>
        </button>
      </div>

      <div className='text-center my-5 authentication-barrier'>
        <span>OR</span>
      </div>

      <form onSubmit={handleSubmit}>
        <div className='mb-3'>
          <label htmlFor='email' className='form-label'>
            Email
          </label>
          <input
            type='email'
            className='form-control form-control-lg'
            id='email'
            name='email'
            placeholder='john@lawfirm.com'
            value={formData.email}
            onChange={handleChange}
            required
          />
        </div>

        <div className='mb-3'>
          <div className='d-flex justify-content-between align-items-center'>
            <label htmlFor='password' className='form-label mb-0'>
              Password
            </label>
            <Link to='/forgot-password' className='text-primary small'>
              Forget password ?
            </Link>
          </div>
          <input
            type='password'
            className='form-control form-control-lg'
            id='password'
            name='password'
            placeholder='Your password'
            value={formData.password}
            onChange={handleChange}
            required
          />
        </div>

        <div className='mb-4'>
          <div className='form-check'>
            <input
              type='checkbox'
              className='form-check-input'
              id='rememberPassword'
              name='rememberPassword'
              checked={formData.rememberPassword}
              onChange={handleChange}
            />
            <label className='form-check-label' htmlFor='rememberPassword'>
              Remember password ?
            </label>
          </div>
        </div>

        <button
          type='submit'
          className='btn btn-primary btn-lg w-100'
          disabled={loading}
        >
          {loading ? 'Signing in...' : 'Sign In'}
        </button>

        <div className='mt-4 text-center'>
          <p className='mb-0'>
            Don&apos;t have an account?{' '}
            <Link to='/register' className='text-primary'>
              Sign Up
            </Link>
          </p>
        </div>
      </form>
    </>
  );
};

export default Login;
